import {ObjectKeys} from '@/utilities/helpers'
import {useDiseases} from '@/features/treatment-requests/services/useDiseases'
import {DiseaseSmall, TreatmentRequestDiagnosisValidationSchema} from '@/features/treatment-requests/types'
import {diseasesGroupNameToLabel} from '@/features/treatment-requests/utils'
import {Controller, UseFieldArrayReplace, useFormContext} from 'react-hook-form'
import {FormFieldBox} from '@/components/ui/form-atoms/FormAtoms'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {useTranslation} from '@/translations/i18n'
import {useAsync} from '@/hooks/useAsync'
import {httpGetDisease} from '@/features/treatment-requests/services/treatment-requests.http'
import {StyledDeceasesGroupsGrid} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {DiseasesGroupsSelectorLoader} from '@/features/treatment-requests/components/diseases-groups-selectors-loader/DiseasesGroupsSelectorsLoader'
import {PatientAgeRange} from '@/types'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'

interface DiseasesGroupsRadioButtonsProps {
    serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
    onChange: UseFieldArrayReplace<TreatmentRequestDiagnosisValidationSchema, 'diseases'>
}

export const DiseasesGroupsRadioButtons = ({serviceType, onChange}: DiseasesGroupsRadioButtonsProps) => {
    const {t} = useTranslation()
    const form = useFormContext<TreatmentRequestDiagnosisValidationSchema>()

    const diseasesGroupsQuery = useDiseases(
        {
            serviceType,
            limit: 15,
            responseType: 'small'
        },
        {enabled: !!serviceType}
    )

    const selectedGroupDetailsQuery = useAsync()

    const diseasesGroupsMap = Object.fromEntries(
        diseasesGroupsQuery.remappedData.map(disease => [disease.name, disease])
    ) satisfies Record<DiseaseSmall['name'], DiseaseSmall>

    const onSelectGroup = async (diseaseId: DiseaseSmall['id']) => {
        try {
            const {data} = await selectedGroupDetailsQuery.run(
                httpGetDisease({
                    urlParams: {diseaseId},
                    params: {responseType: 'extended'}
                })
            )

            onChange([data])
        } catch (error) {
            console.error(error)
        }
    }

    if (diseasesGroupsQuery.isLoading) {
        return <Spinner size={16} />
    }

    if (diseasesGroupsQuery.isError || !diseasesGroupsQuery.remappedData) {
        return <InputHelpText error={t('errors:default')} />
    }

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            <StyledDeceasesGroupsGrid columns={6} gap={3} fullWidth>
                {diseasesGroupsQuery.isLoading
                    ? Array.from({length: 6}, (_, index) => <Skeleton key={index} width="100%" height={36} />)
                    : ObjectKeys(diseasesGroupsMap)
                          // filtered to ensure that groupkey is used as the key for diseasesGroupNameToLabel
                          .filter(groupKey => groupKey in diseasesGroupNameToLabel)
                          .map(groupKey => {
                              const currentGroup = diseasesGroupsMap[groupKey]
                              const labelKey = groupKey as keyof typeof diseasesGroupNameToLabel
                              return (
                                  <Controller
                                      key={groupKey}
                                      control={form.control}
                                      name="diseases"
                                      render={({field}) => (
                                          <FormFieldBox
                                              spanColumn={1}
                                              hasError={!!form.formState.errors.diseases}
                                              isSelected={field.value?.some(disease => disease.id == currentGroup.id)}
                                          >
                                              <RadioField
                                                  label={t(diseasesGroupNameToLabel[labelKey])}
                                                  name={currentGroup.id}
                                                  type="checkbox"
                                                  checked={field.value?.some(disease => disease.id == currentGroup.id)}
                                                  onChange={() => {
                                                      onChange([currentGroup])
                                                      onSelectGroup(currentGroup.id)
                                                  }}
                                              />
                                          </FormFieldBox>
                                      )}
                                  />
                              )
                          })}
            </StyledDeceasesGroupsGrid>
            {selectedGroupDetailsQuery.isError && (
                <InputHelpText error={t('treatment_requests:diseases_groups_error')} />
            )}
            {selectedGroupDetailsQuery.isLoading && <DiseasesGroupsSelectorLoader />}

            {form.formState.errors.diseases && <InputHelpText error={form.formState.errors.diseases.message} />}
        </Flexbox>
    )
}
